/**
 * Footer Component
 * Handles the newsletter section background image with proper CDN paths
 */

import { getAssetPath, testImageLoad } from '../../utils/asset-path';
import ee from '../../ee';

class Footer {
  constructor(container = document) {
    this.container = container;
    this.newsletterSection = container.querySelector('.shopify-section--newsletter');
    this.newsletterContent = this.newsletterSection?.querySelector('.newsletter .content');
    
    // Don't initialize if newsletter section doesn't exist
    if (!this.newsletterSection || !this.newsletterContent) return;
    
    this.diagonalLinesPath = getAssetPath('bg-diagonal-lines.png');
    
    // Store bound callback for proper cleanup
    this.themeLoadedCallback = this.setupNewsletter.bind(this);
    
    // Ensure the border is transparent until image loads
    if (this.newsletterContent) {
      this.newsletterContent.style.borderColor = 'transparent';
    }
    
    this.init();
  }
  
  init() {
    this.setupNewsletter();
    this.bindEvents();
  }
  
  setupNewsletter() {
    // First test if the image is available
    testImageLoad(this.diagonalLinesPath)
      .then(success => {
        if (!success) {
          console.warn('Could not load diagonal lines background image');
          return;
        }
        
        // Apply the background image with proper CDN path
        this.applyNewsletterBorderImage();
      })
      .catch(error => {
        console.error('Error testing image availability:', error);
      });
  }
  
  applyNewsletterBorderImage() {
    // Check if current brand should have background images
    const currentBrand = this.getCurrentBrand();
    if (currentBrand === 'connaught' || currentBrand === 'berkeley') {
      console.log('Skipping newsletter background image for', currentBrand, 'brand');
      return;
    }
    
    // Create and apply styles for the content element using background image approach
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .shopify-section--newsletter .newsletter .content {
        position: relative !important;
        background-image: url('${this.diagonalLinesPath}') !important;
        background-size: cover !important;
        background-position: center !important;
        background-repeat: no-repeat !important;
        border: none !important;
        padding: 20px !important;
        box-sizing: border-box !important;
      }
      
      .shopify-section--newsletter .newsletter .content::after {
        content: '' !important;
        position: absolute !important;
        top: 20px !important;
        left: 20px !important;
        right: 20px !important;
        bottom: 20px !important;
        background-color: white !important;
        z-index: 1 !important;
      }
      
      .shopify-section--newsletter .newsletter .content > * {
        position: relative !important;
        z-index: 2 !important;
        padding: 2rem !important;
        margin: 0 !important;
      }
      
      @media screen and (min-width: 750px) {
        .shopify-section--newsletter .newsletter .content {
          padding: 25px !important;
        }
        
        .shopify-section--newsletter .newsletter .content::after {
          top: 25px !important;
          left: 25px !important;
          right: 25px !important;
          bottom: 25px !important;
        }
        
        .shopify-section--newsletter .newsletter .content > * {
          padding: 2.5rem !important;
        }
      }
    `;
    document.head.appendChild(styleElement);
    
    console.log('Applied newsletter background image with cover effect:', this.diagonalLinesPath);
  }
  
  getCurrentBrand() {
    // Check for brand class on body or html element
    const body = document.body;
    const html = document.documentElement;
    
    if (body.classList.contains('brand-connaught') || html.classList.contains('brand-connaught')) {
      return 'connaught';
    }
    if (body.classList.contains('brand-berkeley') || html.classList.contains('brand-berkeley')) {
      return 'berkeley';
    }
    
    // Default to claridges
    return 'claridges';
  }
  
  bindEvents() {
    // Listen for theme:loaded event
    ee.addListener('theme:loaded', this.themeLoadedCallback);
    
    // Listen for section:load to handle dynamic section updates
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId && event.detail.container.classList.contains('shopify-section--newsletter')) {
        this.newsletterSection = event.detail.container;
        this.newsletterContent = this.newsletterSection.querySelector('.newsletter .content');
        if (this.newsletterContent) {
          // Ensure the border is transparent
          this.newsletterContent.style.borderColor = 'transparent';
          this.setupNewsletter();
        }
      }
    });
  }
  
  destroy() {
    // Remove event listeners
    ee.removeListener('theme:loaded', this.themeLoadedCallback);
  }
}

// Initialize the footer component
const footer = new Footer();

export default Footer; 